<template>
  <v-card outlined>
    <v-card-title>
      Timeline RAM
      <v-spacer />
      <div class="d-flex align-center">
        <v-btn-toggle
          v-model="rangeDays"
          mandatory
          dense
          color="primary"
          class="mr-3"
        >
          <v-btn :value="1">24h</v-btn>
          <v-btn :value="3">3j</v-btn>
          <v-btn :value="7">7j</v-btn>
          <v-btn :value="30">30j</v-btn>
        </v-btn-toggle>
        <v-btn icon @click="reload" :loading="loading" :title="'Recharger'">
          <v-icon>{{ icons.mdiRefresh }}</v-icon>
        </v-btn>
      </div>
    </v-card-title>

    <v-card-text v-if="loading && series[0].data.length === 0" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" />
    </v-card-text>
    <v-card-text
      v-else-if="series[0].data.length === 0"
      class="text-center text--secondary py-8"
    >
      Aucun snapshot sur la période.
    </v-card-text>
    <div v-else>
      <vue-apex-charts
        :key="chartKey"
        type="line"
        height="380"
        :options="chartOptions"
        :series="series"
        @markerClick="onMarkerClick"
      />
    </div>

    <v-card-text class="pt-0 pb-2 caption text--secondary d-flex align-center flex-wrap">
      <span class="mr-3">
        <v-icon small color="grey">{{ icons.mdiCircle }}</v-icon> baseline
      </span>
      <span class="mr-3">
        <v-icon small color="orange">{{ icons.mdiCircle }}</v-icon> threshold
      </span>
      <span class="mr-3">
        <v-icon small color="purple">{{ icons.mdiCircle }}</v-icon> swap
      </span>
      <span class="ml-auto">Cliquez un point pour ouvrir le détail.</span>
    </v-card-text>
  </v-card>
</template>

<script>
import { mdiCircle, mdiRefresh } from "@mdi/js";
import axios from "@axios";
import VueApexCharts from "vue-apexcharts";

const COLOR_BY_TRIGGER = {
  baseline: "#9E9E9E",
  threshold: "#FB8C00",
  swap: "#8E24AA",
};

const RADIUS_BY_TRIGGER = {
  baseline: 3,
  threshold: 7,
  swap: 7,
};

export default {
  name: "MemUsageTimelineChart",
  components: { VueApexCharts },
  data() {
    return {
      rangeDays: 1,
      loading: false,
      snapshots: [],
      chartKey: 0,
      icons: { mdiCircle, mdiRefresh },
    };
  },
  computed: {
    series() {
      const data = this.snapshots
        .slice()
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        .map((s) => ({
          x: new Date(s.created_at).getTime(),
          y: +(s.mem_used_mb / 1024).toFixed(2),
          snapshotId: s.id,
          trigger: s.trigger_type,
          palier: s.threshold_gb,
          swapUsedMb: s.swap_used_mb,
        }));
      return [{ name: "RAM used (Go)", data }];
    },
    discreteMarkers() {
      return this.series[0].data.map((point, idx) => ({
        seriesIndex: 0,
        dataPointIndex: idx,
        fillColor: COLOR_BY_TRIGGER[point.trigger] || "#9E9E9E",
        strokeColor: "#fff",
        size: RADIUS_BY_TRIGGER[point.trigger] || 3,
      }));
    },
    chartOptions() {
      return {
        chart: {
          type: "line",
          toolbar: { show: true, tools: { download: true } },
          zoom: { enabled: true },
          animations: { enabled: false },
        },
        stroke: { curve: "smooth", width: 2 },
        colors: ["#1E88E5"],
        markers: {
          size: 0,
          discrete: this.discreteMarkers,
          hover: { size: 9 },
        },
        xaxis: {
          type: "datetime",
          labels: { datetimeUTC: false },
        },
        yaxis: {
          title: { text: "RAM used (Go)" },
          decimalsInFloat: 1,
          min: 0,
        },
        tooltip: {
          x: { format: "dd MMM HH:mm" },
          custom: ({ seriesIndex, dataPointIndex, w }) => {
            const point = w.config.series[seriesIndex].data[dataPointIndex];
            const date = new Date(point.x);
            return `
              <div style="padding:8px 12px">
                <div><b>${date.toLocaleString("fr-FR")}</b></div>
                <div>RAM: <b>${point.y.toFixed(2)} Go</b></div>
                <div>Trigger: <b>${point.trigger}</b>${
                  point.trigger !== "baseline"
                    ? ` (palier ${point.palier} Go)`
                    : ""
                }</div>
                ${
                  point.swapUsedMb
                    ? `<div>Swap used: ${(point.swapUsedMb / 1024).toFixed(2)} Go</div>`
                    : ""
                }
                <div style="opacity:.7;font-size:11px">Cliquez pour le détail</div>
              </div>
            `;
          },
        },
        grid: { strokeDashArray: 3 },
      };
    },
  },
  watch: {
    rangeDays() {
      this.reload();
    },
  },
  async mounted() {
    await this.reload();
  },
  methods: {
    async reload() {
      this.loading = true;
      try {
        const to = new Date();
        const from = new Date(to.getTime() - this.rangeDays * 86400 * 1000);
        const { data } = await axios.get("/system/ram-snapshots/", {
          params: {
            limit: 500,
            from_date: from.toISOString(),
            to_date: to.toISOString(),
          },
        });
        this.snapshots = data.items || [];
        this.chartKey += 1; // force re-render to refresh discrete markers
      } catch (e) {
        this.snapshots = [];
      } finally {
        this.loading = false;
      }
    },
    onMarkerClick(_event, _chart, { seriesIndex, dataPointIndex }) {
      const point = this.series[seriesIndex].data[dataPointIndex];
      if (point && point.snapshotId) {
        this.$emit("snapshot-click", point.snapshotId);
      }
    },
  },
};
</script>
